import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Bug, Trash2, Server, Cpu } from 'lucide-react';
import { DeviceExecutionLog } from '@/components/workflow/DeviceExecutionLog';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';

export const WorkflowExecutionTab: React.FC = () => {
  const {
    selectedTarget,
    workflowId,
    serverDebugMode,
    toggleServerDebugMode,
    lastExecutionId,
    clearExecutionLogs,
    debugModeEnabled,
    setDebugModeEnabled,
    selectedDeviceId,
    devices,
    setDeviceLogging,
  } = useWorkflowEditor();

  return (
    <div className="flex-1 flex flex-col">
      {/* Debug Mode Controls - Server Workflows */}
      {selectedTarget === 'server' && (
        <>
          <div className="p-3 border-b space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="server-debug-mode" className="flex items-center gap-2 text-sm font-medium">
                <Bug className="w-4 h-4" />
                Debug Mode
              </Label>
              <Switch
                id="server-debug-mode"
                checked={serverDebugMode}
                onCheckedChange={(checked) => toggleServerDebugMode(checked)}
                disabled={!workflowId}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {serverDebugMode
                ? 'Real-time step events will be shown during execution'
                : 'Enable to see step-by-step execution progress'}
            </p>

            {serverDebugMode && lastExecutionId && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  clearExecutionLogs(lastExecutionId);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Debug State
              </Button>
            )}
          </div>

          {/* Server Execution Log */}
          <div className="flex-1 p-2 overflow-hidden">
            {lastExecutionId ? (
              <DeviceExecutionLog
                executionId={lastExecutionId}
                maxHeight="calc(100vh - 350px)"
                title="Server Execution"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No execution yet</p>
                <p className="text-xs mt-1">
                  {serverDebugMode
                    ? 'Run the workflow to see execution steps'
                    : 'Enable debug mode and run the workflow'}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Debug Mode Controls - ESP32 Devices */}
      {selectedTarget === 'esp32' && (
        <>
          <div className="p-3 border-b space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="device-debug-mode" className="flex items-center gap-2 text-sm font-medium">
                <Bug className="w-4 h-4" />
                Debug Mode
              </Label>
              <Switch
                id="device-debug-mode"
                checked={debugModeEnabled}
                onCheckedChange={(checked) => {
                  setDebugModeEnabled(checked);
                  if (selectedDeviceId) {
                    const device = devices.find(d => d.id === selectedDeviceId);
                    if (device) {
                      setDeviceLogging(device.deviceId, checked);
                    }
                  }
                }}
                disabled={!selectedDeviceId}
              />
            </div>

            {debugModeEnabled && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  const device = devices.find(d => d.id === selectedDeviceId);
                  if (device) {
                    clearExecutionLogs(device.deviceId);
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Debug State
              </Button>
            )}
          </div>

          {/* Device Execution Log */}
          <div className="flex-1 p-2 overflow-hidden">
            {selectedDeviceId ? (
              <DeviceExecutionLog
                deviceId={devices.find(d => d.id === selectedDeviceId)?.deviceId || ''}
                maxHeight="calc(100vh - 350px)"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                <Cpu className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No device selected</p>
                <p className="text-xs mt-1">Select a device to see execution logs</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
